"use client";

import React from "react";
import Image, { type StaticImageData } from "next/image";
import { useTheme } from "@/HomeFeatures/Context/ThemeContext";

interface ServicesProps {
  title1: string;
  title2: React.ReactNode;
  description: React.ReactNode;
  servicesArray: ServiceItem[];
  sources?: string[];
}

type ServiceItem = {
  icon: string | StaticImageData;
  title: string;
  description: string;
  highlightText?: string;
};

export default function AiServicesNew({
  title1,
  title2,
  description,
  servicesArray,
  sources = ["Firewall Digital", "MagnetTaba", "Forbes", "McKinsey"],
}: ServicesProps) {
  const { handleOpenQuote } = useTheme();

  return (
    <section className="relative bg-[#d9e2ed]">
      <div className="w-[calc(100%_-_10%)] mx-auto max-w-[2500px] flex flex-col gap-[50px] lg:py-[100px] py-[70px]">
        {/* ── Header row ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[30px] items-start">
          <div className="flex flex-col gap-[12px]">
            <span className="text-[length:var(--primarybtn)] font-medium uppercase tracking-widest text-[#00040b]">
              {title1}
            </span>
            <h2 className="clash-font font-medium leading-[120%] text-[#00040b] text-[length:var(--section-title)]">
              {title2}
            </h2>
          </div>
          <p className="text-[#00040b]/70 text-[length:var(--hero-para)] leading-[170%]">
            {description}
          </p>
        </div>

        {/* ── Cards grid ── */}
        <div className="bg-white border border-[#dce5f0] rounded-2xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 divide-y divide-[#e8eef7] xl:divide-y-0">
            {servicesArray?.map((item, index) => (
              <div
                key={item.title}
                className={[
                  "group relative p-8 lg:p-[42px] flex flex-col gap-[26px]",
                  "transition-all duration-300 ease-out",
                  "hover:bg-[#f0f5fd]",
                  // vertical dividers between columns on xl
                  "xl:[&:not(:nth-child(3n))]:border-r xl:border-r-[#e8eef7]",
                  // horizontal divider between the two rows on xl
                  index >= 3 ? "xl:border-t xl:border-t-[#e8eef7]" : "",
                ].join(" ")}
              >
                {/* Subtle top-left accent on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-[inherit] bg-gradient-to-br from-[#225BD4]/5 to-transparent" />

                {/* Badge: icon + stat */}
                <div className="inline-flex items-center gap-3 bg-[#dbe5f3] group-hover:bg-[#c8d8f5] px-4 py-3 rounded-2xl w-fit transition-colors duration-300">
                  <Image
                    src={item.icon}
                    alt={item.title}
                    width={36}
                    height={36}
                    className="w-[38px] h-[38px] object-contain"
                    priority
                    fetchPriority="high"
                  />
                  {item.highlightText && (
                    <span className="text-[#225bd4] text-[28px] lg:text-[32px] clash-font font-semibold leading-none tracking-tight">
                      {item.highlightText}
                    </span>
                  )}
                </div>

                {/* Text */}
                <div className="flex flex-col gap-[10px]">
                  <h3 className="text-[20px] lg:text-[24px] clash-font font-semibold leading-[130%] text-[#00040b]">
                    {item.title}
                  </h3>
                  <p
                    className="text-[15px] lg:text-[19px] leading-[160%] text-[#636364]"
                    dangerouslySetInnerHTML={{ __html: item.description ?? "" }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* ── Sources row — inside the white box ── */}
          {sources && sources.length > 0 && (
            <div className="border-t border-[#e8eef7] px-8 lg:px-[42px] py-4 flex flex-col md:flex-row items-start md:items-center gap-2.5">
              <div className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#225bd4"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="shrink-0 opacity-70"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <span className="font-semibold text-md text-[#225bd4]">
                  Sources:
                </span>
              </div>
              <span className="text-md text-[#636364]">
                {sources.join(", ")}
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
